import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button } from 'reactstrap';
import { useFormContext } from 'react-hook-form';

import {
  guidelineDeliveryTypeOptions,
  guidelineTypeOptions,
  languageOptions,
  monthNumberLabel,
} from '../../../../constants';
import SelectInput from 'components/Select-Input';
import SpinnerInput from 'components/Spinner-Input';
import TextInput from 'components/Text-Input';
import { ContentCardLayout } from './styles';
import DisplayErrors from 'components/Display-Errors';
import ContainerDatePicker from '../Container-Date-Picker';
import { formatDateTimestampNumber } from '../../../../utils';

const currentMonth = new Date().getMonth() + 1;
const currentYear = new Date().getFullYear();
const nextYear = currentYear + 1;

const yearOptions = [
  { label: currentYear.toString(), value: currentYear },
  { label: nextYear.toString(), value: nextYear },
];

const ContentCard = ({ index, canDelete, onDelete }) => {
  const { t: translate } = useTranslation();
  const {
    formState: { errors },
    clearErrors,
    watch,
    setValue,
  } = useFormContext();
  const [selectedMonth, setSelectedMonth] = useState(
    monthNumberLabel.find((month) => month.value === currentMonth),
  );
  const [selectedYear, setSelectedYear] = useState(yearOptions[0]);
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [publishedDate, setPublishedDate] = useState(null);
  const [isOpenInput, setIsOpenInput] = useState(null);
  const [isReviewLinkRequired, setIsReviewLinkRequired] = useState(false);

  const handleSelectMonth = (value) => {
    const month = monthNumberLabel.find((month) => month.value === value);
    setSelectedMonth(month);
  };

  const handleSelectYear = (value) => {
    const year = yearOptions.find((year) => year.value === value);
    setSelectedYear(year);

    if (year) {
      setValue(`guidelines.${index}.productionDate.year`, year, { shouldValidate: true });
    } else {
      setValue(`guidelines.${index}.productionDate.year`, null, { shouldValidate: true });
    }
  };

  const handleErrors = (errors) => {
    if (!errors) return {};

    if (errors.type === 'required') {
      return { required: translate('Required Field!') };
    }

    if (errors.type === 'pattern') {
      return { invalidUrl: translate('Invalid URL!') };
    }

    if (errors.message) {
      return { custom: translate(errors.message) };
    }

    return _.isString(errors) ? { custom: translate(errors) } : errors;
  };

  const validateTotalSize = (value) => {
    if (value < 100) {
      return 'Must be greater than or equal to 100';
    }

    if (value >= 100) {
      clearErrors(`guidelines.${index}.totalSize`);
    }

    return true;
  };

  const getSelectErrorStyle = (hasError) => ({
    control: (provided) => ({
      ...provided,
      borderColor: hasError ? '#e53e3e' : '#cccccc',
      borderWidth: hasError ? '2px' : '1px',
      '&:hover': {
        borderColor: hasError ? '#e53e3e' : '#bfbfbf',
      },
    }),
  });

  const getInputErrorStyle = (hasError) => ({
    borderColor: hasError ? '#e53e3e' : '#ced4da',
    borderWidth: hasError ? '2px' : '1px',
  });

  useEffect(() => {
    const deliveryType = watch(`guidelines.${index}.deliveryType.value`);

    setIsReviewLinkRequired(deliveryType === 'REVIEW' || deliveryType === 'REVIEW_AND_DIRECTIONS');
  }, [watch(`guidelines.${index}.deliveryType`)]);

  useEffect(() => {
    const predefinedProductionMonth = watch(`guidelines.${index}.productionDate.month`);
    const predefinedProductionYear = watch(`guidelines.${index}.productionDate.year`);
    const predefinedDeliveryDate = watch(`guidelines.${index}.deliveryDate`);
    const predefinedPublishedDate = watch(`guidelines.${index}.publishedDate`);

    // Month Handling
    if (typeof predefinedProductionMonth !== 'object') {
      // Value from CSV or raw string
      const numericMonth = Number(predefinedProductionMonth);
      const month = monthNumberLabel.find((m) => m.value === numericMonth);
      setSelectedMonth(month || null);
    } else {
      setSelectedMonth(predefinedProductionMonth);
    }

    // Year Handling
    if (typeof predefinedProductionYear === 'object') {
      if (predefinedProductionYear?.value) {
        // Valid value from CSV or raw string
        const year = yearOptions.find((y) => y.value === predefinedProductionYear.value);
        setSelectedYear(year);
      } else {
        // Invalid value from CSV
        setSelectedYear(null);
        setValue(`guidelines.${index}.productionDate.year`, null, {
          shouldValidate: true,
        });
      }
    } else if (predefinedProductionYear) {
      // Raw value from defaultValues
      const numericYear = Number(predefinedProductionYear);
      const year = yearOptions.find((y) => y.value === numericYear);
      setSelectedYear(year);
      setValue(`guidelines.${index}.productionDate.year`, year, {
        shouldValidate: true,
      });
    } else {
      // Manual creation without value
      const currentYear = new Date().getFullYear();
      const year = yearOptions.find((y) => y.value === currentYear);
      setSelectedYear(year);
      setValue(`guidelines.${index}.productionDate.year`, year, {
        shouldValidate: true,
      });
    }

    setDeliveryDate(predefinedDeliveryDate || null);
    setPublishedDate(predefinedPublishedDate || null);
  }, [
    watch(`guidelines.${index}.deliveryDate`),
    watch(`guidelines.${index}.publishedDate`),
    watch(`guidelines.${index}.productionDate.month`),
    watch(`guidelines.${index}.productionDate.year`),
  ]);

  return (
    <ContentCardLayout>
      <div className="card-form">
        <div className="d-flex w-100" style={{ width: '100%', justifyContent: 'space-between' }}>
          <div className="d-flex flex-column" style={{ minWidth: '40%' }}>
            <SelectInput
              label="Type"
              controlName={`guidelines.${index}.deliveryType`}
              size={3}
              required
              options={guidelineDeliveryTypeOptions}
              minWidth="100%"
              styles={getSelectErrorStyle(!!errors?.guidelines?.[index]?.deliveryType)}
            />
            <DisplayErrors error={handleErrors(errors?.guidelines?.[index]?.deliveryType)} />
          </div>
          <div className="d-flex flex-column" style={{ minWidth: '56%' }}>
            <TextInput
              type="text"
              label="URL"
              controlName={`guidelines.${index}.reviewLink`}
              size={4}
              placeholder="Insert the link to the content to be reviewed"
              noEllipsis
              required={isReviewLinkRequired}
              minWidth="100%"
              style={{
                ...getInputErrorStyle(!!errors?.guidelines?.[index]?.reviewLink),
              }}
              validation={{
                ...(isReviewLinkRequired && { required: 'Required Field!' }),
                pattern: {
                  value: /^(https?:\/\/)?([\w\-]+\.)+[\w]{2,}(\/\S*)?$/,
                  message: 'Invalid URL!',
                },
              }}
            />
            <DisplayErrors error={handleErrors(errors?.guidelines?.[index]?.reviewLink)} />
          </div>
        </div>
        <div className="d-flex w-100 gap-4">
          <div className="d-flex flex-column" style={{ minWidth: '30%' }}>
            <SelectInput
              label="Format"
              controlName={`guidelines.${index}.type`}
              size={4}
              required
              options={guidelineTypeOptions}
              minWidth="100%"
              styles={getSelectErrorStyle(!!errors?.guidelines?.[index]?.type)}
            />
            <DisplayErrors error={handleErrors(errors?.guidelines?.[index]?.type)} />
          </div>
          <div className="d-flex flex-column" style={{ minWidth: '30%' }}>
            <SelectInput
              label="Language"
              controlName={`guidelines.${index}.language`}
              size={3}
              required
              options={languageOptions}
              minWidth="100%"
              styles={getSelectErrorStyle(!!errors?.guidelines?.[index]?.language)}
            />
            <DisplayErrors error={handleErrors(errors?.guidelines?.[index]?.language)} />
          </div>
          <div className="d-flex flex-column w-100" style={{ minWidth: '100%' }}>
            <SpinnerInput
              controlName={`guidelines.${index}.totalSize`}
              labelTwo="Number of words"
              size={4}
              validation={{
                validate: (value) => validateTotalSize(value),
              }}
              step={100}
              required
              freeInput
              error={handleErrors(errors?.guidelines?.[index]?.totalSize)}
            />
            <DisplayErrors error={handleErrors(errors?.guidelines?.[index]?.totalSize)} />
          </div>
        </div>
        <div className="d-flex w-100 gap-4 align-items-start">
          <div className="d-flex gap-2 align-items-start m-0" style={{ width: '100%' }}>
            <div className="d-flex flex-column justify-content-end" style={{ minWidth: '50%' }}>
              <SelectInput
                label="Production month"
                controlName={`guidelines.${index}.productionDate.month`}
                size={7}
                required
                options={monthNumberLabel}
                maxWidth="100%"
                minWidth="100%"
                value={selectedMonth}
                onChange={handleSelectMonth}
                styles={getSelectErrorStyle(!!errors?.guidelines?.[index]?.productionDate?.month)}
              />
              <DisplayErrors
                error={handleErrors(errors?.guidelines?.[index]?.productionDate?.month)}
              />
            </div>
            <div className="d-flex flex-column justify-content-end" style={{ minWidth: '50%' }}>
              <div style={{ width: '1rem', height: '2.23rem' }} />
              <SelectInput
                label=""
                controlName={`guidelines.${index}.productionDate.year`}
                size={5}
                required
                options={yearOptions}
                maxWidth="100%"
                minWidth="100%"
                value={selectedYear}
                onChange={handleSelectYear}
                styles={getSelectErrorStyle(!!errors?.guidelines?.[index]?.productionDate?.year)}
                isClearable={false}
              />
              <DisplayErrors
                error={handleErrors(errors?.guidelines?.[index]?.productionDate?.year)}
              />
            </div>
          </div>

          <div
            className="position-relative d-flex flex-column"
            style={{ minWidth: '25%', maxWidth: '25%' }}
            onMouseLeave={() => setIsOpenInput(null)}
          >
            <TextInput
              label="Delivery date"
              value={
                deliveryDate ? formatDateTimestampNumber(deliveryDate, translate('en-US')) : ''
              }
              controlName={`guidelines.${index}.deliveryDate`}
              size={3}
              placeholder={
                deliveryDate
                  ? formatDateTimestampNumber(deliveryDate, translate('en-US'))
                  : 'Select...'
              }
              noEllipsis
              required
              minWidth="100%"
              readOnly
              style={{
                ...getInputErrorStyle(!!errors?.guidelines?.[index]?.deliveryDate),
                cursor: 'pointer',
              }}
              onClick={() => setIsOpenInput('deliveryDate')}
            />
            <DisplayErrors error={handleErrors(errors?.guidelines?.[index]?.deliveryDate)} />
            {isOpenInput === 'deliveryDate' && (
              <ContainerDatePicker
                date={deliveryDate}
                setDate={setDeliveryDate}
                index={index}
                propertyName={isOpenInput}
              />
            )}
          </div>
          <div
            className="position-relative"
            style={{ minWidth: '25%', maxWidth: '25%' }}
            onMouseLeave={() => setIsOpenInput(null)}
          >
            <TextInput
              label="Publication date"
              value={
                publishedDate ? formatDateTimestampNumber(publishedDate, translate('en-US')) : ''
              }
              controlName={`guidelines.${index}.publishedDate`}
              size={3}
              placeholder={
                publishedDate
                  ? formatDateTimestampNumber(publishedDate, translate('en-US'))
                  : 'Select...'
              }
              noEllipsis
              minWidth="100%"
              readOnly
              style={{ cursor: 'pointer' }}
              onClick={() => setIsOpenInput('publishedDate')}
            />
            {isOpenInput === 'publishedDate' && (
              <ContainerDatePicker
                date={publishedDate}
                setDate={setPublishedDate}
                index={index}
                propertyName={isOpenInput}
                anyDate
              />
            )}
          </div>
        </div>
        <div className="d-flex gap-4 w-100 justify-content-between" style={{ width: '100%' }}>
          <div className="d-flex flex-column" style={{ width: '100%', minWidth: '70%' }}>
            <TextInput
              label="Main Keyword"
              controlName={`guidelines.${index}.primaryKeyword`}
              size={7}
              placeholder="Enter a possible title for your guideline"
              noEllipsis
              required
              minWidth="100%"
              maxWidth="100%"
              style={{
                ...getInputErrorStyle(!!errors?.guidelines?.[index]?.primaryKeyword),
              }}
            />
            <DisplayErrors error={handleErrors(errors?.guidelines?.[index]?.primaryKeyword)} />
          </div>
          <div className="d-flex flex-column" style={{ width: '100%', maxWidth: '25%' }}>
            <TextInput
              label="Search Volume"
              controlName={`guidelines.${index}.searchVolume`}
              size={3}
              type="number"
              placeholder=""
              noEllipsis
              required
              minWidth="100%"
              maxWidth="100%"
              style={{
                ...getInputErrorStyle(!!errors?.guidelines?.[index]?.searchVolume),
              }}
            />
            <DisplayErrors
              error={handleErrors(errors?.guidelines?.[index]?.searchVolume)}
              style={{ display: 'flex', flexWrap: 'wrap' }}
            />
          </div>
        </div>
        <div style={{ width: '100%' }}>
          <TextInput
            label="Secondary Keywords"
            controlName={`guidelines.${index}.secondaryKeywords`}
            size={12}
            placeholder="Enter the secondary keywords separated by commas"
            noEllipsis
          />
          <p className="text-detail">{translate('Separate key words with commas.')}</p>
        </div>
        {canDelete && (
          <div className="full-width">
            <Button className="btn-delete" onClick={() => onDelete(index)}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M4.49834 6.00167C4.49834 5.72553 4.27448 5.50167 3.99834 5.50167C3.7222 5.50167 3.49834 5.72553 3.49834 6.00167H4.49834ZM12.5017 6.00167C12.5017 5.72553 12.2778 5.50167 12.0017 5.50167C11.7255 5.50167 11.5017 5.72553 11.5017 6.00167H12.5017ZM9.8339 6.66861C9.8339 6.39247 9.61004 6.16861 9.3339 6.16861C9.05775 6.16861 8.8339 6.39247 8.8339 6.66861H9.8339ZM8.8339 11.3372C8.8339 11.6134 9.05775 11.8372 9.3339 11.8372C9.61004 11.8372 9.8339 11.6134 9.8339 11.3372H8.8339ZM7.16612 6.66861C7.16612 6.39247 6.94226 6.16861 6.66612 6.16861C6.38998 6.16861 6.16612 6.39247 6.16612 6.66861H7.16612ZM6.16612 11.3372C6.16612 11.6134 6.38998 11.8372 6.66612 11.8372C6.94226 11.8372 7.16612 11.6134 7.16612 11.3372H6.16612ZM2.99792 3.50084C2.72178 3.50084 2.49792 3.72469 2.49792 4.00084C2.49792 4.27698 2.72178 4.50084 2.99792 4.50084V3.50084ZM13.0021 4.50084C13.2782 4.50084 13.5021 4.27698 13.5021 4.00084C13.5021 3.72469 13.2782 3.50084 13.0021 3.50084V4.50084ZM4.85789 3.84272C4.77056 4.10469 4.91214 4.38785 5.17412 4.47518C5.43609 4.5625 5.71925 4.42092 5.80657 4.15895L4.85789 3.84272ZM5.69505 2.91238L6.16939 3.0705L6.16943 3.07038L5.69505 2.91238ZM6.96091 2L6.96079 2.5H6.96091V2ZM9.03911 2V2.5L9.03973 2.5L9.03911 2ZM10.3063 2.91238L10.7808 2.75479L10.7807 2.75438L10.3063 2.91238ZM10.1933 4.15843C10.2803 4.42049 10.5633 4.56239 10.8254 4.47535C11.0874 4.38832 11.2293 4.10531 11.1423 3.84324L10.1933 4.15843ZM3.49834 6.00167V12.6711H4.49834V6.00167H3.49834ZM3.49834 12.6711C3.49834 13.6839 4.3194 14.505 5.33223 14.505V13.505C4.87169 13.505 4.49834 13.1317 4.49834 12.6711H3.49834ZM5.33223 14.505H10.6678V13.505H5.33223V14.505ZM10.6678 14.505C11.6806 14.505 12.5017 13.6839 12.5017 12.6711H11.5017C11.5017 13.1317 11.1283 13.505 10.6678 13.505V14.505ZM12.5017 12.6711V6.00167H11.5017V12.6711H12.5017ZM8.8339 6.66861V11.3372H9.8339V6.66861H8.8339ZM6.16612 6.66861V11.3372H7.16612V6.66861H6.16612ZM2.99792 4.50084H13.0021V3.50084H2.99792V4.50084ZM5.80657 4.15895L6.16939 3.0705L5.22071 2.75427L4.85789 3.84272L5.80657 4.15895ZM6.16943 3.07038C6.2829 2.7297 6.60171 2.49992 6.96079 2.5L6.96103 1.5C6.17134 1.49981 5.47021 2.00516 5.22067 2.75438L6.16943 3.07038ZM6.96091 2.5H9.03911V1.5H6.96091V2.5ZM9.03973 2.5C9.39911 2.49956 9.71836 2.72941 9.83192 3.07038L10.7807 2.75438C10.5309 2.00452 9.82885 1.49902 9.03849 1.5L9.03973 2.5ZM9.83179 3.06997L10.1933 4.15843L11.1423 3.84324L10.7808 2.75479L9.83179 3.06997Z"
                  fill="#F56565"
                />
              </svg>
            </Button>
          </div>
        )}
      </div>
    </ContentCardLayout>
  );
};

ContentCard.propTypes = {
  index: PropTypes.number.isRequired,
  canDelete: PropTypes.bool,
  onDelete: PropTypes.func,
};

export default ContentCard;
